import { leerToken, guardarToken, guardarTokenDeAdmin, borrarToken } from '../utils/sesion';
import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

function hexToRgb(h) {
  h = (h || '').replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgba(h, a) { const [r, g, b] = hexToRgb(h); return `rgba(${r},${g},${b},${a})`; }
function shade(h, p) { // p>0 aclara, p<0 oscurece
  let [r, g, b] = hexToRgb(h);
  const t = p < 0 ? 0 : 255, amt = Math.abs(p);
  r = Math.round((t - r) * amt + r); g = Math.round((t - g) * amt + g); b = Math.round((t - b) * amt + b);
  return `rgb(${r},${g},${b})`;
}

const norm = (h) => { if (!h) return h; h = String(h).trim(); return h[0] === '#' ? h : '#' + h; };

function luminance(h) {
  const [r, g, b] = hexToRgb(h).map(v => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function toHex(rgb) { const m = rgb.match(/\d+/g).map(Number); return '#' + m.map(v => v.toString(16).padStart(2, '0')).join(''); }

// Color principal ajustado para que se lea bien sobre el fondo del modo
function accentFor(primary, theme) {
  let c = primary;
  for (let i = 0; i < 6; i++) {
    const L = luminance(c);
    if (theme === 'light' && L > 0.45) c = toHex(shade(c, -0.15));
    else if (theme === 'dark' && L < 0.06) c = toHex(shade(c, 0.2));
    else break;
  }
  return c;
}

const THEME_VARS = ['--accent', '--border-focus', '--accent-glow', '--accent-hover', '--bg-primary', '--bg-secondary', '--bg-card', '--bg-hover', '--bg-input', '--text-primary', '--text-secondary', '--text-muted', '--border'];

// Solo el color principal (y la tipografía) vienen del cliente; fondos y textos son genéricos por modo
function applyTenantBranding(tenant, theme = currentTheme()) {
  const root = document.documentElement;
  THEME_VARS.forEach(v => root.style.removeProperty(v));
  const primary = norm(tenant && tenant.primary_color) || '#2E7DE2';
  const accent = accentFor(primary, theme);
  root.style.setProperty('--accent', accent);
  root.style.setProperty('--border-focus', accent);
  root.style.setProperty('--accent-glow', rgba(accent, theme === 'light' ? 0.1 : 0.14));
  root.style.setProperty('--accent-hover', toHex(shade(accent, theme === 'light' ? -0.12 : 0.15)));
  if (tenant && tenant.font_family) root.style.setProperty('--font-family', tenant.font_family);
}

function systemTheme() {
  return 'light'; // el claro es el predeterminado; cada usuario elige el suyo
}
function currentTheme() { return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'; }
function storedTheme() { try { return localStorage.getItem('crm_theme'); } catch { return null; } }
function setDomTheme(theme) {
  document.documentElement.dataset.theme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'light' ? '#ffffff' : '#050b1a');
}
// aplicar antes del primer render para evitar parpadeo
setDomTheme(storedTheme() || 'light');

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setThemeState] = useState(currentTheme());

  const applyTheme = (t, tenantArg) => {
    const next = t === 'light' ? 'light' : 'dark';
    setDomTheme(next);
    try { localStorage.setItem('crm_theme', next); } catch {}
    setThemeState(next);
    applyTenantBranding(tenantArg !== undefined ? tenantArg : tenant, next);
  };
  // lo elige el usuario: se guarda en su cuenta
  const setTheme = (t) => { applyTheme(t); api.put('/auth/theme', { theme: t }).catch(() => {}); };

  useEffect(() => {
    /* Entrada desde el super admin: el enlace trae la sesión en ?ver=. Se guarda
       como cualquier otra y se limpia la URL, para que el token no quede a la
       vista ni se comparta sin querer al copiar la dirección. */
    const params = new URLSearchParams(window.location.search);
    const desdeAdmin = params.get('ver');
    if (desdeAdmin) {
      // va a sessionStorage: la sesión muere cuando cerrás la pestaña
      guardarTokenDeAdmin(desdeAdmin);
      params.delete('ver');
      const limpia = window.location.pathname + (params.toString() ? '?' + params : '');
      window.history.replaceState({}, '', limpia);
    }
    const token = leerToken();
    const savedTenant = localStorage.getItem('crm_tenant');
    if (savedTenant) {
      try { const t = JSON.parse(savedTenant); setTenant(t); applyTenantBranding(t); } catch {}
    }
    if (token) {
      api.get('/auth/verify')
        .then(res => {
          setUser(res.data.user);
          if (res.data.user.theme) applyTheme(res.data.user.theme, res.data.tenant || null);
          if (res.data.tenant) {
            setTenant(res.data.tenant);
            localStorage.setItem('crm_tenant', JSON.stringify(res.data.tenant));
            applyTenantBranding(res.data.tenant);
          }
        })
        .catch(() => borrarToken())
        .finally(() => setLoading(false));
    } else { setLoading(false); }
  }, []);

  // Deja la sesión armada. Lo usan el ingreso con contraseña y el ingreso con código.
  const abrirSesion = (data) => {
    guardarToken(data.token);
    setUser(data.user);
    if (data.user.theme) applyTheme(data.user.theme, data.tenant || null);
    if (data.tenant) {
      setTenant(data.tenant);
      localStorage.setItem('crm_tenant', JSON.stringify(data.tenant));
      applyTenantBranding(data.tenant);
    }
    return data;
  };

  const login = async (username, password) => {
    const res = await api.post('/auth/login', { username, password });
    return abrirSesion(res.data);
  };

  const logout = () => { borrarToken(); setUser(null); setTenant(null); };

  return <AuthContext.Provider value={{ user, tenant, login, logout, abrirSesion, loading, theme, setTheme }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
